/* eslint-disable react/no-unescaped-entities */
import { Heart, HandHeart, Users, Wrench } from "lucide-react";

import { useSeo } from "@/lib/seo";

export default function About() {
  useSeo(
    "Our Story | Pit Stop Motors — Family-Owned in Nampa, Idaho",
    "Pit Stop Motors is a family-owned, faith-rooted used car dealership in Nampa, Idaho. Read our story and what we stand for."
  );
  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="bg-primary text-primary-foreground">
        <div className="mx-auto max-w-7xl px-6 md:px-10 py-20 md:py-28">
          <p className="overline !text-accent-bright">Our Mission</p>
          <h1 className="mt-3 font-serif text-5xl md:text-6xl tracking-tight leading-none">
            A small Idaho lot<br /><span className="italic text-accent-bright">with old-school values.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-primary-foreground/75 leading-relaxed">
            Pit Stop Motors is a family-owned, faith-rooted dealership. We believe a car purchase should feel like buying from a neighbor &mdash; honest, easy, and genuinely helpful.
          </p>
        </div>
      </section>

      {/* MISSION STATEMENT */}
      <section className="border-b border-soft bg-card" data-testid="about-mission-statement">
        <div className="mx-auto max-w-5xl px-6 md:px-10 py-20 md:py-24 text-center">
          <p className="overline">Mission Statement</p>
          <p className="mt-6 font-serif text-3xl md:text-4xl lg:text-5xl text-primary leading-tight tracking-tight">
            &ldquo;To make car buying feel like buying from a neighbor &mdash; honest, unhurried, and built on a real handshake.&rdquo;
          </p>
          <p className="mt-6 text-sm uppercase tracking-[0.2em] text-muted-foreground">
            &mdash; The Pit Stop Motors Family
          </p>
        </div>
      </section>

      {/* OUR STORY */}
      <section className="mx-auto max-w-7xl px-6 md:px-10 py-20 grid lg:grid-cols-12 gap-12" data-testid="about-story">
        <div className="lg:col-span-5">
          <p className="overline">Our Story</p>
          <h2 className="mt-3 font-serif text-4xl md:text-5xl text-primary tracking-tight">
            Kids dreaming <br /> about cars, <br /> building it honest.
          </h2>
        </div>
        <div className="lg:col-span-7 space-y-5 text-muted-foreground leading-relaxed text-lg">
          <p>
            Growing up in Idaho, we were just like everyone else — kids dreaming about cars, wandering through local lots, and imagining the day we’d have one of our own. But even back then, we noticed something that didn’t sit right. Not every dealership felt honest. Not every conversation felt genuine. And not every buyer walked away feeling confident about the car they were taking home.
          </p>
          <p>Those early experiences stuck with us.</p>
          <p>
            As lifelong car fanatics, we’ve always loved finding a new project, fixing things up, and appreciating what a reliable vehicle can mean for someone’s daily life. So when the opportunity came to open the doors of Pit Stop Motors, we knew exactly what we wanted to build — <span className="text-primary font-medium">a place where honesty wasn’t optional, where customers felt like neighbors, and where trust came standard.</span>
          </p>
          <p>
            Pit Stop Motors was founded on the belief that buying a car should feel good. It should feel fair. It should feel like you’re working with people who genuinely care about you and your family. That’s why we’ve committed ourselves to being family‑friendly, neighborhood‑friendly, and community‑focused in everything we do.
          </p>
          <p>
            We’re proud to be local, faith‑based, and deeply connected to the community that raised us. Supporting the people around us isn’t just part of our business — it’s part of who we are. Our mission is simple: provide reliable vehicles, honest service, and a dealership experience that feels like home.
          </p>
          <p className="font-serif text-2xl text-primary">Welcome to Pit Stop Motors. We’re glad you’re here.</p>
        </div>
      </section>

      {/* BENTO IMAGE GRID */}
      <section className="bg-card border-y border-soft">
        <div className="mx-auto max-w-7xl px-6 md:px-10 py-20 grid md:grid-cols-2 gap-6" data-testid="about-bento">
          <div className="p-8 md:p-10 bg-primary text-primary-foreground flex flex-col justify-center">
            <p className="overline !text-accent-bright">Our Promise</p>
            <p className="mt-4 font-serif text-2xl md:text-3xl italic leading-snug">
              "We'll treat you like a neighbor &mdash; because that's exactly what you are."
            </p>
            <p className="text-xs uppercase tracking-wider text-primary-foreground/60 mt-3">The Pit Stop Team</p>
          </div>
          <div className="p-8 md:p-10 border border-soft bg-background">
            <p className="overline">Idaho Born</p>
            <h3 className="mt-3 font-serif text-3xl text-primary">Local to the bone.</h3>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
              I was born and raised right here in Idaho. This place and its people <em>are</em> my family. Pit Stop Motors is local through and through &mdash; owned, run, and rooted in this community. When you buy a car from us, you're not just a customer. You're a neighbor, and we take care of our own.
            </p>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="mx-auto max-w-7xl px-6 md:px-10 py-20" data-testid="about-values">
        <p className="overline">What We Stand For</p>
        <h2 className="mt-3 font-serif text-4xl md:text-5xl text-primary tracking-tight">Four promises we keep.</h2>
        <div className="mt-12 grid md:grid-cols-2 gap-10">
          {[
            { Icon: Heart, title: "Integrity Above All", body: "Every car has a story. We tell you the whole story — the good and the bad — so you can make a confident decision." },
            { Icon: Wrench, title: "Inspected Right", body: "Every vehicle gets a thorough multi-point inspection before it hits the lot. If something needs attention, we tell you up front." },
            { Icon: Users, title: "Hospitality", body: "Coffee is always on. Conversations are always welcome. Whether you buy a car today or in five years, you're a neighbor here." },
            { Icon: HandHeart, title: "Community First", body: "We're a local business serving local families. We give back to Idaho communities through the people and causes we care about." },
          ].map((v, i) => (
            <div key={i} className="border border-soft p-8 bg-card" data-testid={`about-value-${i}`}>
              <v.Icon className="h-7 w-7 text-accent" strokeWidth={1.4} />
              <h3 className="mt-4 font-serif text-2xl text-primary">{v.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{v.body}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
